import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, MoreVertical, RotateCcw, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Props {
  onNewChat: () => void;
  /** "Talk to the team"; omit when the surface shows its own team button. */
  onEscalate?: () => void;
  /** Where "Continue in Messages" goes (signed-in users on the home widget); omit to hide it. */
  continueTo?: string;
  /** Raise the menu and the confirm above the z-[100] home overlay. */
  elevated?: boolean;
}

/** The "…" menu both concierge surfaces share: New chat, Talk to the team. */
const ConciergeThreadMenu = ({ onNewChat, onEscalate, continueTo, elevated = false }: Props) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const z = elevated ? "z-[110]" : undefined;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-9 w-9 text-muted-foreground shrink-0"
            aria-label="Conversation options"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={z}>
          <DropdownMenuItem className="gap-2" onSelect={() => setConfirmOpen(true)}>
            <RotateCcw className="h-4 w-4" /> New chat
          </DropdownMenuItem>
          {onEscalate && (
            <DropdownMenuItem className="gap-2" onSelect={onEscalate}>
              <Users className="h-4 w-4" /> Talk to the team
            </DropdownMenuItem>
          )}
          {continueTo && (
            <DropdownMenuItem className="gap-2" asChild>
              <Link to={continueTo}>
                <MessageSquare className="h-4 w-4" /> Continue in Messages
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent overlayClassName={z} className={z}>
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This conversation stays saved for our team, but it won't show here anymore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep chatting</AlertDialogCancel>
            <AlertDialogAction onClick={onNewChat}>Start new chat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConciergeThreadMenu;
